import { useMemo } from "react";
import { getUser, hasRole, hasRoles, isRoot } from "../../../../core/Global";
import SideMenu, { ISideMenuItem } from "../../../../components/layouts/aside/SideMenu";
import { FontAwesomeIcons } from "../../../../core/Types";
import { UserTypes } from "../../../../core/Enums";


interface IGroup {
    name: string
    slug: string
    visible: boolean
    items: Array<{
        id: string
        name: string
        icon: FontAwesomeIcons
        iconType?: "solid" | "regular" | "light" | "thin" | "brands"
        visible: boolean
        fullPath?: string
    }>
}


export function usePreferencesSideMenuItems(): ISideMenuItem[] {

    const user = getUser()
    const isSystemAdmin = !!user?.isSystemAdmin
    const canSettings = hasRole("SystemSettings")
    const isOwner = user?.userType == UserTypes.Customer

    return useMemo<ISideMenuItem[]>(() => {

        const groups: IGroup[] = [
            {
                name: "Account",
                slug: "account",
                visible: true,
                items: [
                    { id: "overview", name: "Account Overview", icon: "table-columns", visible: true },
                    { id: "edit-account", name: "Edit Account", icon: "user", visible: true },
                    { id: "security", name: "Security & Login", icon: "lock", visible: true },
                    { id: "edit-owner", name: "Edit Owner", icon: "user-tie", visible: isSystemAdmin },
                    { id: "company", name: "Company Settings", icon: "briefcase", visible: isSystemAdmin },
                    // The signed-in user's own settings.
                    { id: "preferences", name: "Preferences", icon: "sliders", visible: true },
                    { id: "notifications", name: "Notifications", icon: "bell-on", visible: true },
                    { id: "notification-history", name: "Notification History", icon: "list", visible: true },
                    { id: "login-rules", name: "Login Rules", icon: "right-to-bracket", visible: isOwner },
                ]
            },
            {
                name: "Finance",
                slug: "finance",
                visible: canSettings,
                items: [
                    { id: "payment-methods", name: "Payment Methods", icon: "credit-card", visible: true },
                    { id: "subscription", name: "Subscription", icon: "badge-check", visible: true },
                    { id: "wallet", name: "Wallet", icon: "wallet", visible: true },
                    { id: "invoices", name: "Invoices", icon: "file-invoice-dollar", visible: true },
                ]
            },
            {
                name: "Usages",
                slug: "usages",
                visible: canSettings,
                items: [
                    { id: "messages", name: "Messages", icon: "messages", visible: true },
                    { id: "calls", name: "Calls", icon: "phone-volume", visible: true },
                ]
            },
            {
                name: "General",
                slug: "general",
                visible: true,
                items: [
                    { id: "announcements", name: "Announcements", icon: "megaphone", visible: hasRoles(["ManageAnnouncements"]), fullPath: "/system/announcements" },
                    { id: "firewall", name: "Firewall Whitelist", icon: "shield-halved", visible: hasRoles(["ManageFirewall"]), fullPath: "/system/firewall/whitelist" },
                    { id: "integrations", name: "API & Integrations", icon: "code-merge", visible: canSettings },
                    { id: "coupons", name: "Coupons", icon: "ticket", visible: isRoot() },
                    { id: "servers", name: "PBX Servers", icon: "server", visible: isRoot(), fullPath: "/system/servers" },
                    { id: "wainstances", name: "WA Instances", icon: "whatsapp", iconType: "brands", visible: isRoot(), fullPath: "/system/wainstances" },
                    { id: "others", name: "Other Settings", icon: "cog", visible: canSettings },
                    { id: "mobileapps", name: "App Management", icon: "mobile-screen-button", visible: isRoot(), fullPath: "/system/mobileapps" },
                ]
            },
        ]

        const result: ISideMenuItem[] = []
        for (const g of groups) {
            if (!g.visible) continue
            const visible = g.items.filter(it => it.visible)
            if (visible.length === 0) continue
            result.push({ seperator: true, name: g.name })
            for (const it of visible) {
                result.push({
                    id: `${g.slug}-${it.id}`,
                    name: it.name,
                    icon: it.icon,
                    iconType: it.iconType,
                    link: it.fullPath ?? `/system/${g.slug}/${it.id}`
                })
            }
        }
        return result

    }, [isSystemAdmin, canSettings, isOwner])
}


export default function PreferencesSideMenu() {
    const items = usePreferencesSideMenuItems()
    return <SideMenu items={items} />
}
